// Course related type definitions

export interface Duration {
  hours?: number;
  minutes?: number;
  seconds?: number;
}

export interface LearningObjective {
  id?: string;
  _id?: string;
  objective?: string;
  description?: string;
  bloom_level?: string;
  measurable?: boolean;
}

export interface Activity {
  id?: string;
  _id?: string;
  title: string;
  description: string;
  type: string;
  estimated_time?: Duration;
  instructions?: string[];
}

export interface Practice {
  id?: string;
  title: string;
  description: string;
  difficulty?: string;
  starter_code?: string;
  solution?: string;
  hints?: string[];
}

export interface Assessment {
  id?: string;
  _id?: string;
  title: string;
  type: string;
  passing_score: number;
  questions?: any[];
}

export interface SynchronizedCaption {
  enabled?: boolean;
  words?: Array<{
    word: string;
    start_time: number;
    end_time: number;
    confidence?: number;
  }>;
  sentences?: any[];
}

export interface PedagogicalElements {
  learning_objective?: string;
  bloom_level?: string;
  interaction_type?: string;
  assessment_hook?: string;
}

export interface Scene {
  _id?: string;
  scene_number?: number;
  order?: number;
  title?: string;
  duration: number;
  html_content?: string;
  layout_type?: string;
  visual_description?: string;
  narration?: string;
  media_urls?: {
    s3_video_url?: string;
    s3_audio_url?: string;
    s3_html_url?: string;
    thumbnail_url?: string;
  };
  synchronized_captions?: SynchronizedCaption;
  pedagogical_elements?: PedagogicalElements;
  scene_data?: SceneData;
  is_regenerating?: boolean;
  error_message?: string;
}

export type LayoutType =
  | 'title'
  | 'content'
  | 'bullet'
  | 'code'
  | 'comparison'
  | 'image_text'
  | 'summary'
  | 'qa'
  | 'timeline'
  | 'stats'
  | 'quote'
  | 'process';

export interface SceneData {
  layout_type: LayoutType;
  heading: string;
  subheading?: string;
  body_text?: string;
  bullet_points?: string[];
  code_snippet?: { language: string; code: string };
  image_urls?: string[];
  key_takeaways?: string[];
  comparison?: {
    left: { title: string; points: string[] };
    right: { title: string; points: string[] };
  };
  icon?: string;
  tag_label?: string;
  callout_text?: string;
  highlight_words?: string[];
  question?: string;
  answer?: string;
  steps?: { title: string; description?: string }[];
  stats?: { value: string; label: string }[];
  quote_text?: string;
  attribution?: string;
}

export interface ContentAssessmentRef {
  id: string;
  title?: string;
  description?: string;
  required?: boolean;
  passing_score?: number;
  total_questions?: number;
}

export interface ContentItem {
  _id?: string;
  id?: string;
  title: string;
  type: ContentDeliveryType | string;
  content?: string;
  duration?: Duration;
  estimated_time?: Duration;
  scene_count?: number;
  scenes?: Scene[];
  audio_url?: string;
  description?: string;
  interactive_elements?: InteractiveElementData[];
  learning_checks?: any[];
  language?: string;
  pre_assessment?: ContentAssessmentRef;
  post_assessment?: ContentAssessmentRef;

  // Enhanced fields for HTML slides (from API response)
  slide_structure?: ApiSlideData[];
  presentation_config?: PresentationConfig;
  rich_media_assets?: ApiRichMediaAssets;
  assessment_questions?: ApiAssessmentQuestion[];
  assessment_config?: AssessmentConfig;
  media_assets?: {
    slides_url?: string;
    s3_html_url?: string;
    s3_audio_url?: string;
    s3_video_url?: string;
    s3_captioned_video_url?: string;
    s3_subtitle_url?: string;
  };

  // Origin of the content. 'uploaded' = built from an uploaded PDF/Word file →
  // read-only (no HTML editor / AI regenerate), only deletable.
  content_source?: 'authored' | 'uploaded';
  uploaded_file?: {
    name?: string;
    url?: string;
    file_type?: string;
    uploaded_at?: string;
  };
}

// Enhanced Content Item interfaces for interactive slides
export interface SlideData {
  id: string;
  order: number;
  title: string;
  layout_type: 'title' | 'content' | 'media' | 'split' | 'full-media' | 'code' | 'interactive';
  html_content: string;
  css_styles?: string;
  animations?: AnimationConfig[];
  media_embeds?: MediaEmbed[];
  interactive_elements?: InteractiveElementData[];
  duration_hint?: number; // Suggested time in seconds
  prerequisites?: string[]; // Required previous slides
}

export interface PresentationConfig {
  theme: 'educational' | 'modern' | 'corporate' | 'dark' | 'minimal';
  transition_type: 'fade' | 'slide' | 'zoom' | 'flip' | 'cube';
  auto_advance: boolean;
  controls_visible: boolean;
  progress_type: 'bar' | 'dots' | 'both' | 'none';
  keyboard_navigation: boolean;
  touch_gestures: boolean;
}

export interface RichMediaAssets {
  images: ImageAsset[];
  videos: VideoAsset[];
  audio: AudioAsset[];
  diagrams: DiagramAsset[];
  documents: DocumentAsset[];
}

export interface ImageAsset {
  id: string;
  url: string;
  alt?: string;
  caption?: string;
  width?: number;
  height?: number;
}

export interface VideoAsset {
  id: string;
  url: string;
  thumbnail?: string;
  duration?: number;
  caption?: string;
  controls?: boolean;
  autoplay?: boolean;
  loop?: boolean;
}

export interface AudioAsset {
  id: string;
  url: string;
  duration?: number;
  caption?: string;
  controls?: boolean;
  autoplay?: boolean;
  loop?: boolean;
}

export interface DiagramAsset {
  id: string;
  type: 'mermaid' | 'svg' | 'image';
  definition: string;
  caption?: string;
}

export interface DocumentAsset {
  id: string;
  url: string;
  type: string;
  filename: string;
  size?: number;
}

export interface MediaEmbed {
  id: string;
  type: 'image' | 'video' | 'audio' | 'diagram' | 'iframe' | 'code';
  source: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'background';
  size: 'small' | 'medium' | 'large' | 'full';
  controls?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  caption?: string;
}

export interface InteractiveElementData {
  id: string;
  type: 'quiz' | 'code-editor' | 'drag-drop' | 'form' | 'poll' | 'simulation';
  config: any;
  position: 'inline' | 'overlay' | 'sidebar';
  required: boolean;
  points?: number;
}

export interface AnimationConfig {
  type: 'fade' | 'slide' | 'zoom' | 'rotate' | 'bounce';
  duration: number;
  delay?: number;
  easing?: string;
  trigger?: 'load' | 'click' | 'scroll' | 'hover';
}

export interface AssessmentQuestion {
  id: string;
  type: 'multiple-choice' | 'true-false' | 'short-answer' | 'essay' | 'code';
  question: string;
  options?: string[];
  correct_answer: string | string[];
  explanation?: string;
  points: number;
  time_limit?: number;
}

export interface QuizConfig {
  question: string;
  options: string[];
  correct_answer: string;
  explanation?: string;
  points?: number;
}

export interface CodeEditorConfig {
  language: string;
  initial_code: string;
  title: string;
  description: string;
  expected_output?: string;
  test_cases?: TestCase[];
}

export interface TestCase {
  input: string;
  expected_output: string;
  description?: string;
}

export interface DragDropConfig {
  items: DragDropItem[];
  targets: DragDropTarget[];
  instructions: string;
  allow_multiple?: boolean;
}

export interface DragDropItem {
  id: string;
  content: string;
  correct_target?: string;
}

export interface DragDropTarget {
  id: string;
  label: string;
  accepts?: string[];
}

export interface FormConfig {
  fields: FormField[];
  title: string;
  description?: string;
  submit_text?: string;
}

export interface FormField {
  id: string;
  type: 'text' | 'number' | 'email' | 'select' | 'textarea' | 'radio' | 'checkbox';
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  validation?: any;
}

export interface PollConfig {
  question: string;
  options: string[];
  allow_multiple?: boolean;
  show_results?: boolean;
}

// API-specific interfaces that match the content-tree response schema
export interface ApiSlideData {
  _id: string;
  id: string;
  order: number;
  title: string;
  layout_type: string;
  html_content: string;
  css_styles?: string;
  media_embeds: ApiMediaEmbed[];
  interactive_elements: any[];
  duration_hint?: number;
  prerequisites: any[];
  animations: any[];
}

export interface ApiMediaEmbed {
  _id: string;
  id: string;
  type: string;
  source: string;
  position: string;
  size: string;
}

export interface ApiRichMediaAssets {
  images: ApiImageAsset[];
  videos: any[];
  audios: any[];
  diagrams: ApiDiagramAsset[];
  documents: any[];
}

export interface ApiImageAsset {
  _id: string;
  id: string;
  type: string;
  source: string;
  slide_id: string;
  alt_text: string;
  resolved_url: string | null;
  thumbnail_url: string | null;
  screenshot_url: string | null;
}

export interface ApiDiagramAsset {
  _id: string;
  id: string;
  type: string;
  source: string;
  caption: string;
  slide_id: string;
  diagram_type: string;
  resolved_url: string | null;
  rendered_url: string | null;
  source_code: string | null;
}

export interface ApiAssessmentQuestion {
  _id: string;
  id: string;
  question_type: string;
  question_text: string;
  options: string[];
  correct_answers: string[];
  blank_positions: any[];
  sequence_items: any[];
  points: number;
  explanation: string;
  difficulty: string;
  bloom_level: string;
  matching_pairs: any[];
}

export interface AssessmentConfig {
  timing: string;
  required: boolean;
  passing_score: number;
  retry_allowed: boolean;
  max_retries: number;
  show_feedback: boolean;
  shuffle_questions: boolean;
  shuffle_options: boolean;
}

// Conversion utilities from API types to component types
export const convertApiSlideToSlideData = (apiSlide: ApiSlideData): SlideData => ({
  id: apiSlide.id,
  order: apiSlide.order,
  title: apiSlide.title,
  layout_type: apiSlide.layout_type as 'title' | 'content' | 'media' | 'split' | 'full-media' | 'code' | 'interactive',
  html_content: apiSlide.html_content,
  css_styles: apiSlide.css_styles,
  media_embeds: apiSlide.media_embeds.map(convertApiMediaEmbedToMediaEmbed),
  interactive_elements: apiSlide.interactive_elements,
  duration_hint: apiSlide.duration_hint,
  prerequisites: apiSlide.prerequisites
});

export const convertApiMediaEmbedToMediaEmbed = (apiEmbed: ApiMediaEmbed): MediaEmbed => ({
  id: apiEmbed.id,
  type: apiEmbed.type as 'image' | 'video' | 'audio' | 'diagram' | 'iframe' | 'code',
  source: apiEmbed.source,
  position: apiEmbed.position as 'top' | 'bottom' | 'left' | 'right' | 'center' | 'background',
  size: apiEmbed.size as 'small' | 'medium' | 'large' | 'full'
});

export const convertApiAssessmentToAssessmentQuestion = (apiQuestion: ApiAssessmentQuestion): AssessmentQuestion => ({
  id: apiQuestion.id,
  type: apiQuestion.question_type as 'multiple-choice' | 'true-false' | 'short-answer' | 'essay' | 'code',
  question: apiQuestion.question_text,
  options: apiQuestion.options,
  correct_answer: apiQuestion.correct_answers.length === 1 ? apiQuestion.correct_answers[0] : apiQuestion.correct_answers,
  explanation: apiQuestion.explanation,
  points: apiQuestion.points,
  time_limit: undefined // Not provided in API schema
});

export const convertApiRichMediaToRichMedia = (apiRichMedia: ApiRichMediaAssets): RichMediaAssets => ({
  images: apiRichMedia.images.map(img => ({
    id: img.id,
    url: img.resolved_url || img.source,
    alt: img.alt_text,
    caption: undefined,
    width: undefined,
    height: undefined
  })),
  videos: [], // Not implemented in API yet
  audio: [], // Not implemented in API yet
  diagrams: apiRichMedia.diagrams.map(diag => ({
    id: diag.id,
    type: diag.diagram_type as 'mermaid' | 'svg' | 'image',
    definition: diag.source_code || diag.source,
    caption: diag.caption
  })),
  documents: [] // Not implemented in API yet
});

export interface ModulePractices {
  guided?: Practice[];
  independent?: Practice[];
  real_world?: Practice[];
}

export interface Module {
  _id: string;
  title: string;
  description: string;
  estimated_duration?: Duration;
  pre_assessment?: ContentAssessmentRef;
  post_assessment?: ContentAssessmentRef;
  content_stats?: {
    video?: number;
    text?: number;
    audio?: number;
    total?: number;
  };
  learning_objectives?: LearningObjective[];
  activities?: Activity[];
  practices?: ModulePractices;
  assessments?: Assessment[];
  content_items?: ContentItem[];
  order?: number;
}

export interface ModuleDetails {
  module: Module & {
    course?: {
      _id: string;
      title: string;
      slug: string;
      description: string;
    };
  };
  stats?: {
    totalContentItems: number;
    totalScenes: number;
    totalVideos: number;
    totalTexts: number;
    totalAudios: number;
    totalInteractive: number;
    totalExercises: number;
    totalDemos: number;
    totalDuration: Duration;
    mediaStatus?: {
      hasGeneratedVideo: boolean;
      hasGeneratedAudio: boolean;
      completedScenes: number;
      pendingScenes: number;
    };
  };
}

export interface ModuleContent {
  module: Module;
}

export interface CourseDetails {
  _id?: string;
  title: string;
  description: string;
  slug?: string;
  stage: 'generating' | 'draft' | 'published' | 'unpublished';
  difficulty: string;
  category: string;
  category_id?: string;
  category_name?: string;
  targetAudience?: string[];
  target_audience?: string[];
  prerequisites?: string[];
  language?: string;
  language_code?: string;
  /**
   * Translation linkage: set on courses created by translating another course.
   * null/undefined = this is a master (original) course.
   */
  source_course_id?: string | null;
  /** True when the API served translated content rather than the master. */
  is_translation?: boolean;
  /**
   * The MASTER course's language — sent even in a translated response.
   * `language_code` above is the language currently ON SCREEN, so it cannot
   * identify the original.
   */
  master_language_code?: string;
  translated_at?: string;
  tags?: string[];
  moduleCount: number;
  module_count?: number;
  status?: string; // Keep for backward compatibility
  modules?: Module[];
  learning_objectives?: LearningObjective[];
  resources?: any[];
  final_project?: any;
  assessment_overview?: any;
  next_steps?: any;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  banner?: string;
  thumbnail?: string;
  rating?: number;
  enrolledUsers?: number;
  createdAt?: string;
  updatedAt?: string;
  pre_assessment?: ContentAssessmentRef;
  post_assessment?: ContentAssessmentRef;
  generation_settings?: CourseGenerationSettings;
  stats?: {
    totalModules: number;
    totalContentItems: number;
    totalVideos: number;
    totalTexts: number;
    totalAudios: number;
  };
  estimated_duration?: {
    total?: Duration;
    videos?: Duration;
    practice?: Duration;
    assessments?: Duration;
  };
  created_by_user?: {
    user_id: string;
    email?: string;
    full_name?: string;
  };
  content_delivery_type?: string;
}

export interface Resource {
  id?: string;
  _id?: string;
  title: string;
  type: string;
  url: string;
  description?: string;
}

export interface CourseMetadata {
  category?: string;
  level?: string;
  language?: string;
  stage?: 'generating' | 'draft' | 'published' | 'unpublished';
  status?: string; // Keep for backward compatibility
  tags?: string[];
  prerequisites?: string[];
  skills?: string[];
  certificate_available?: boolean;
  certificate_template_id?: string;
  rating?: number;
  total_ratings?: number;
  total_enrollments?: number;
  completion_rate?: number;
  version?: string;
  last_updated?: string;
}

export interface ExpandedSections {
  objectives: boolean;
  activities: boolean;
  practices: boolean;
  assessments: boolean;
  content: boolean;
}

// Generation Settings Types (from course-generation-settings-ui-guide.md)
export enum ContentFormatEnum {
  MARKDOWN = "markdown",
  PLAIN = "plain",
  AUDIO_FRIENDLY = "audio-friendly",
  HTML_SLIDES = "html-slides",
  HTML = "html"
}

export enum SlideThemeEnum {
  EDUCATIONAL = "educational",
  MODERN = "modern",
  CORPORATE = "corporate",
  DARK = "dark",
  MINIMAL = "minimal"
}

export enum ContentTypeEnum {
  VIDEO = "video",
  TEXT = "text",
  AUDIO = "audio",
  INTERACTIVE = "interactive",
  EXERCISE = "exercise",
  DEMO = "demo"
}

export enum ContentDeliveryType {
  HTML = "html",
  HTML_WITH_AUDIO = "html_with_audio",
  VIDEO_PRESENTATION = "video_presentation",
  AVATAR_PRESENTATION = "avatar_presentation"
}

export enum DifficultyTypeEnum {
  BEGINNER = "BEGINNER",
  INTERMEDIATE = "INTERMEDIATE",
  ADVANCED = "ADVANCED",
  EXPERT = "EXPERT"
}

export enum ToneTypeEnum {
  FORMAL = "FORMAL",
  INFORMAL = "INFORMAL",
  PROFESSIONAL = "PROFESSIONAL",
  FRIENDLY = "FRIENDLY",
  PERSUASIVE = "PERSUASIVE",
  INSPIRATIONAL = "INSPIRATIONAL",
  NEUTRAL = "NEUTRAL",
  EDUCATIONAL = "EDUCATIONAL",
  EMPATHETIC = "EMPATHETIC",
  PLAYFUL = "PLAYFUL"
}

export enum PedagogyFrameworkEnum {
  DEFAULT = "DEFAULT",
  ADDIE = "ADDIE",
  BLOOMS_TAXONOMY = "BLOOMS_TAXONOMY",
  GAGNES_NINE_EVENTS = "GAGNES_NINE_EVENTS",
  KOLBS_EXPERIENTIAL = "KOLBS_EXPERIENTIAL",
  CONSTRUCTIVIST = "CONSTRUCTIVIST",
  PROBLEM_BASED = "PROBLEM_BASED",
  FLIPPED_CLASSROOM = "FLIPPED_CLASSROOM",
  MICROLEARNING = "MICROLEARNING",
  COMPETENCY_BASED = "COMPETENCY_BASED",
  ADAPTIVE_LEARNING = "ADAPTIVE_LEARNING"
}

export interface LanguageGenerationConfig {
  language_code: string;
  tts_config?: {
    voice_name: string;
    speaking_rate: number;
    pitch: number;
    volume_gain_db?: number;
  };
  caption_config?: {
    enable_captions: boolean;
    format: string;
    max_duration: number;
    min_duration?: number;
  };
}

export interface HtmlSlidesGenerationConfig {
  enable_interactive_elements?: boolean;
  slide_transition_type?: string;
  theme_customization?: any;
  video_rendering_mode?: 'animated' | 'static';
}

export interface AssessmentGenerationConfig {
  questions_per_content: number;
  immediate_feedback: boolean;
  allow_retry: boolean;
  max_retries: number;
  passing_score: number;
}

export interface EnhancedFeaturesGenerationConfig {
  enable_interactive_elements?: boolean;
  enable_gamification?: boolean;
  enable_collaboration?: boolean;
  points_system?: any;
  achievements?: any;
}

export interface ModuleGenerationConfig {
  content_types?: ContentTypeEnum[];
  max_modules?: number;
  include_assessments?: boolean;
}

export interface CourseGenerationSettings {
  content_format: ContentFormatEnum;
  theme?: SlideThemeEnum;
  orchestration_id: string;
  content_delivery_type?: ContentDeliveryType;
  language_config?: LanguageGenerationConfig;
  html_slides_config?: HtmlSlidesGenerationConfig;
  assessment_config?: AssessmentGenerationConfig;
  enhanced_features?: EnhancedFeaturesGenerationConfig;
  module_generation_config?: ModuleGenerationConfig;
  tone?: ToneTypeEnum;
  difficulty_level?: DifficultyTypeEnum;
  pedagogy_framework?: PedagogyFrameworkEnum;
}